import React, { useState, useEffect, useCallback } from 'react';
import './TradeRoom.css';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import {
  ProductSelector,
  TradeParameters,
  MarketDiscovery,
  MarketSelectionModal,
  ChartSection,
  PositionsSummary
} from './components';
import { MobileProductChips } from './components/mobile/MobileProductChips';
import { MobileParameterPanel } from './components/mobile/MobileParameterPanel';
import { InitializationLoader } from './components/InitializationLoader';
import { quoteService, QuoteResponse } from '../../services/api/quoteService';
import { orderService } from '../../services/api/orderService';
import { useSession } from '../../components/session/SessionManager';
import { useNotifications } from '../../components/notifications';
import { instrumentService } from '../../services/api/instrumentService';
import { productService } from '../../services/api/productService';
import { accountService } from '../../services/api/accountService';
import { ASSET_CLASS_MAP } from '../../types/instrument';

const TradeRoom: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Session and notifications (for mobile trade execution)
  const { primaryAccountId } = useSession();
  const { showSuccess } = useNotifications();
  
  // Initialization state
  const [initializationState, setInitializationState] = useState({
    accounts: false,
    products: false,
    productCategories: false,
    instruments: false,
    sseConnection: false,
    isComplete: false,
    error: null as string | null
  });
  const [isInitializing, setIsInitializing] = useState(false);

  // Product and instrument state
  const [selectedProduct, setSelectedProduct] = useState('rise_fall');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [currentPrice, setCurrentPrice] = useState('--');
  const [stake, setStake] = useState(50);
  const [showMarketModal, setShowMarketModal] = useState(false);
  const [selectedMarketType, setSelectedMarketType] = useState('Forex');
  const [desktopMarketType, setDesktopMarketType] = useState('Forex');
  const [desktopInstruments, setDesktopInstruments] = useState<Array<{
    id: string;
    market: string;
    symbol: string;
    name: string;
    price: string;
    change: string;
  }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasLoadedInitialInstruments, setHasLoadedInitialInstruments] = useState(false);
  const [refreshPositions, setRefreshPositions] = useState<(() => void) | null>(null);

  // Handle position refresh callback
  const handlePositionRefresh = useCallback((refreshFn: () => void) => {
    setRefreshPositions(() => refreshFn);
  }, []);

  // Mobile trade execution state (same as desktop)
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  
  // Product-specific parameters (for mobile)
  const [riseFallDirection, _setRiseFallDirection] = useState<'rise' | 'fall'>('rise');
  const [riseFallDuration, _setRiseFallDuration] = useState('15 minutes');
  const [allowEquals, _setAllowEquals] = useState(false);

  const handleProductSelect = (productId: string) => {
    setSelectedProduct(productId);
  };

  // Trade Room Initialization Flow (BUG-011)
  useEffect(() => {
    if (!primaryAccountId || isInitializing || initializationState.isComplete) {
      return;
    }

    initializeTradeRoom();
  }, [primaryAccountId, isInitializing, initializationState.isComplete]);

  // Sequential initialization as per BUG-011 requirements
  const initializeTradeRoom = async () => {
    setIsInitializing(true);
    
    try {
      // Step 1: Load user accounts
      await accountService.getAccounts();
      setInitializationState(prev => ({ ...prev, accounts: true }));

      // Step 2: Load available products
      await productService.getProducts();
      setInitializationState(prev => ({ ...prev, products: true }));

      // Step 3: Load product categories
      await productService.getProductCategories();
      setInitializationState(prev => ({ ...prev, productCategories: true }));

      // Step 4: Load instruments and set up initial selection
      await loadInitialInstruments();
      setInitializationState(prev => ({ ...prev, instruments: true }));

      // Step 5: Establish SSE connection (placeholder - will be implemented in future)
      // TODO: Implement SSE connection via GET /v1/stream/connect
      // For now, mark as complete to avoid blocking
      setInitializationState(prev => ({ ...prev, sseConnection: true }));

      // Mark initialization as complete
      setInitializationState(prev => ({ ...prev, isComplete: true }));

    } catch (error) {
      console.error('TradeRoom: Initialization failed:', error);
      setInitializationState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Initialization failed'
      }));
    } finally {
      setIsInitializing(false);
    }
  };

  // Load initial default instruments
  const loadInitialInstruments = async () => {
    try {
      // Load a few popular instruments from the default market (Forex)
      const assetClass = ASSET_CLASS_MAP['Forex'];
      const instruments = await instrumentService.getInstrumentsByAssetClass(assetClass);
      
      if (instruments.length > 0) {
        // Take the first 3 instruments as default
        const defaultInstruments = instruments.slice(0, 3).map(instrument => ({
          id: instrument.instrumentId,
          market: instrument.market,
          symbol: instrument.symbol,
          name: instrument.name,
          price: instrument.price,
          change: instrument.change
        }));
        
        setDesktopInstruments(defaultInstruments);
        
        // Auto-select the first instrument
        if (defaultInstruments.length > 0) {
          setSelectedInstrument(defaultInstruments[0].id);
          setCurrentPrice(defaultInstruments[0].price);
        }
        
        setHasLoadedInitialInstruments(true);
      }
    } catch (error) {
      console.error('Failed to load initial instruments:', error);
      throw error; // Re-throw to be caught by initialization handler
    }
  };

  // Live quote effect (mobile only - desktop handled by TradeParameters)
  useEffect(() => {
    if (!isMobile) return; // Only run on mobile

    // Block API calls if no instrument is selected
    if (!selectedInstrument) {
      setQuote(null);
      setQuoteLoading(false);
      setQuoteError('Please select an instrument to get quotes');
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError(null);

    const timeout = setTimeout(async () => {
      const baseReq = {
        instrumentId: selectedInstrument,
        product: selectedProduct,
        stake,
        currency: 'USD',
        product_params: quoteService.formatProductParams(selectedProduct, {
          direction: riseFallDirection,
          allow_equals: allowEquals,
          maturity_type: 'duration',
          duration_unit: 'minutes',
          duration_value: parseInt(riseFallDuration) || 15
        })
      };
      
      try {
        const quote = await quoteService.getQuote(baseReq);
        if (!cancelled) {
          setQuote(quote);
          setQuoteLoading(false);
          setQuoteError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setQuote(null);
          setQuoteLoading(false);
          setQuoteError('Failed to fetch live quote');
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isMobile, selectedProduct, selectedInstrument, stake, riseFallDirection, riseFallDuration, allowEquals, primaryAccountId]);


  const handleBuy = async () => {
    if (!isMobile) {
      // Desktop handled by TradeParameters component
      return;
    }

    // Block trade execution if no instrument is selected
    if (!selectedInstrument) {
      setOrderError('Please select an instrument before placing a trade');
      setShowErrorDialog(true);
      return;
    }

    // Mobile trade execution
    if (!primaryAccountId) {
      setOrderError('Please ensure you are logged in to place trades');
      setShowErrorDialog(true);
      return;
    }

    setIsProcessingOrder(true);
    setOrderError(null);

    try {
      // Create order request
      const request = orderService.createOrderRequest({
        accountId: primaryAccountId,
        instrumentId: selectedInstrument,
        product: selectedProduct,
        stake,
        currency: 'USD',
        productParams: {
          direction: riseFallDirection,
          allow_equals: allowEquals,
          maturity_type: 'duration',
          duration_unit: 'minutes',
          duration_value: parseInt(riseFallDuration) || 15
        }
      });

      const orderResponse = await orderService.placeOrder(request);
      
      setIsProcessingOrder(false);
      
      showSuccess(
        'Trade Placed Successfully!',
        `Your ${getInstrumentSymbol()} ${riseFallDirection} order (#${orderResponse.orderId}) has been placed with a stake of $${orderResponse.stake.toFixed(2)}.`
      );
      
      // Refresh positions after successful trade
      if (refreshPositions && typeof refreshPositions === 'function') {
        refreshPositions();
      }
      
    } catch (error: any) {
      setOrderError(error.message || 'Failed to place order');
      setIsProcessingOrder(false);
      setShowErrorDialog(true);
    }
  };


  // Get instrument symbol for display
  const getInstrumentSymbol = () => {
    // Find the selected instrument from the desktop instruments list
    const selectedInstrumentData = desktopInstruments.find(inst => inst.id === selectedInstrument);
    return selectedInstrumentData?.symbol || selectedInstrument || 'No instrument selected';
  };

  // Mock positions data (removed - using real API data)


  // Open the market selection modal to change instrument
  const handleInstrumentSelect = () => {
    setShowMarketModal(true);
  };

  const handleExpandChart = () => {
    // Expand chart to full screen
  };

  const handleMarketTypeChange = async (market: string) => {
    setDesktopMarketType(market);
    setSelectedMarketType(market);
    
    // Load instruments for the new market type if we don't have any for this market
    const hasInstrumentsForMarket = desktopInstruments.some(inst => inst.market === market);
    
    if (!hasInstrumentsForMarket && primaryAccountId) {
      try {
        const assetClass = ASSET_CLASS_MAP[market as keyof typeof ASSET_CLASS_MAP];
        if (assetClass) {
          const instruments = await instrumentService.getInstrumentsByAssetClass(assetClass);
          
          if (instruments.length > 0) {
            // Take the first 3 instruments from this market
            const newInstruments = instruments.slice(0, 3).map(instrument => ({
              id: instrument.instrumentId,
              market: instrument.market,
              symbol: instrument.symbol,
              name: instrument.name,
              price: instrument.price,
              change: instrument.change
            }));
            
            // Add these instruments to the existing list
            setDesktopInstruments(prev => [...prev, ...newInstruments]);
          }
        }
      } catch (error) {
        console.error(`Failed to load instruments for ${market}:`, error);
      }
    }
  };

  const handleRemoveInstrument = (symbol: string) => {
    setDesktopInstruments(desktopInstruments.filter(i => i.symbol !== symbol));
  };

  const handleInstrumentSelection = (instrument: { id?: string; market: string; symbol: string; name: string; price: string; change: string }) => {
    // Only add if not already present
    if (instrument.id && !desktopInstruments.some(i => i.id === instrument.id)) {
      const instrumentWithId = {
        id: instrument.id,
        market: instrument.market,
        symbol: instrument.symbol,
        name: instrument.name,
        price: instrument.price,
        change: instrument.change
      };
      setDesktopInstruments([...desktopInstruments, instrumentWithId]);
    }
    
    // Update selected instrument and price
    if (instrument.id) {
      setSelectedInstrument(instrument.id);
    }
    
    setCurrentPrice(instrument.price);
    setShowMarketModal(false);
  };

  // Handle retry initialization
  const handleRetryInitialization = () => {
    setInitializationState({
      accounts: false,
      products: false,
      productCategories: false,
      instruments: false,
      sseConnection: false,
      isComplete: false,
      error: null
    });
    initializeTradeRoom();
  };

  // Show initialization loading state
  if (isInitializing || !initializationState.isComplete) {
    return (
      <div className="trade-room">
        <InitializationLoader
          initializationState={initializationState}
          onRetry={handleRetryInitialization}
        />
      </div>
    );
  }

  return (
    <div className="trade-room">
      {/* Mobile Product Selector */}
      {isMobile && (
        <MobileProductChips
          selectedProduct={selectedProduct}
          onProductSelect={handleProductSelect}
        />
      )}

      {/* Left Sidebar - Products Selection (Web Only) */}
      {!isMobile && (
        <ProductSelector
          selectedProduct={selectedProduct}
          selectedInstrument={selectedInstrument}
          onProductSelect={handleProductSelect}
        />
      )}

      {/* Main Area */}
      <main className="trade-main-area">
        {/* Market Discovery Row (Desktop Only) */}
        {!isMobile && (
          <MarketDiscovery
            isMobile={isMobile}
            selectedMarketType={selectedMarketType}
            selectedProduct={selectedProduct}
            selectedInstrument={selectedInstrument}
            currentPrice={currentPrice}
            desktopMarketType={desktopMarketType}
            desktopInstruments={desktopInstruments}
            onShowMarketModal={() => setShowMarketModal(true)}
            onMarketTypeChange={handleMarketTypeChange}
            onRemoveInstrument={handleRemoveInstrument}
            onInstrumentSelect={handleInstrumentSelection}
          />
        )}

        {/* Market Selection Modal */}
        <MarketSelectionModal
          isOpen={showMarketModal}
          isMobile={isMobile}
          selectedMarketType={selectedMarketType}
          selectedProduct={selectedProduct}
          searchQuery={searchQuery}
          selectedInstrument={selectedInstrument}
          onClose={() => setShowMarketModal(false)}
          onMarketTypeSelect={setSelectedMarketType}
          onSearchChange={setSearchQuery}
          onInstrumentSelect={handleInstrumentSelection}
        />

        {/* Loading indicator for initial instruments */}
        {!hasLoadedInitialInstruments && !isMobile && (
          <div className="instruments-loading">
            Loading initial instruments...
          </div>
        )}

        {/* Chart Section */}
        <ChartSection
          isMobile={isMobile}
          selectedInstrument={selectedInstrument}
          currentPrice={currentPrice}
          selectedProduct={selectedProduct}
          onInstrumentSelect={handleInstrumentSelect}
          onExpandChart={handleExpandChart}
        />

        {/* Open Positions Summary (Web Only) */}
        {!isMobile && <PositionsSummary onRefreshReady={handlePositionRefresh} />}
      </main>

      {/* Right Sidebar - Trade Parameters (Web Only) */}
      {!isMobile && (
        <TradeParameters 
          selectedProduct={selectedProduct}
          selectedInstrument={selectedInstrument}
          onTradeSuccess={refreshPositions}
        />
      )}

      {/* Mobile Quote Status */}
      {isMobile && (quoteLoading || quoteError || quote) && (
        <div className={`mobile-quote-status ${quoteError ? 'error' : 'loading'}`}>
          {quoteLoading ? '🔄 Loading live quote...' : 
           quoteError ? `⚠️ ${quoteError}` :
           quote ? `💰 Payout: $${quote.payout.toFixed(2)} (${quote.return_percent?.toFixed(1) || '0.0'}%)` :
           ''}
        </div>
      )}

      {/* Mobile Parameter Panel */}
      {isMobile && (
        <MobileParameterPanel
          selectedProduct={selectedProduct}
          stake={stake}
          onStakeChange={setStake}
          onBuy={handleBuy}
        />
      )}

      {/* Mobile Processing Indicator */}
      {isMobile && isProcessingOrder && (
        <div className="mobile-processing-overlay">
          <div className="processing-spinner">
            <div className="spinner"></div>
            <p>Processing your trade...</p>
          </div>
        </div>
      )}

      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="modal-overlay">
          <div className="error-dialog">
            <div className="error-header">
              <h3>Trade Error</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowErrorDialog(false)}
              >
                ×
              </button>
            </div>
            <div className="error-content">
              <p>{orderError}</p>
            </div>
            <div className="error-actions">
              <button 
                className="error-ok-btn" 
                onClick={() => setShowErrorDialog(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TradeRoom;
export { TradeRoom };
